import { z } from "zod";

const normalizePathBaseUrl = (value: unknown, fallback: string): string => {
	if (typeof value !== "string" || !value.trim()) return fallback;
	const trimmed = value.trim();

	// Preserve absolute API URLs so the frontend can target the backend explicitly in dev.
	if (/^https?:\/\//i.test(trimmed)) {
		try {
			const url = new URL(trimmed);
			const normalizedPath = normalizePathBaseUrl(url.pathname, fallback);
			return `${url.origin}${normalizedPath}`;
		} catch {
			return fallback;
		}
	}

	// Ensure leading slash and no trailing slash.
	const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
	const withoutTrailingSlash =
		withLeadingSlash.endsWith("/") && withLeadingSlash !== "/" ? withLeadingSlash.slice(0, -1) : withLeadingSlash;

	// Backends in this repo are mounted at "/api/v1". If someone provides "/v1" (or ".../v1"),
	// treat it as shorthand and normalize it to "/api/v1" to avoid 404s like "/v1/auth/login".
	if (withoutTrailingSlash === "/v1") return "/api/v1";
	if (withoutTrailingSlash.endsWith("/v1") && !withoutTrailingSlash.includes("/api/")) {
		return `${withoutTrailingSlash.slice(0, -3)}/api/v1`;
	}

	return withoutTrailingSlash;
};

const normalizeSocketUrl = (value: unknown): string => {
	if (typeof value !== "string") return "";
	return value.trim();
};

const configSchema = z.object({
	VITE_API_BASE_URL: z
		.string()
		.optional()
		.transform((value) => normalizePathBaseUrl(value, "/api/v1")),

	// Optional. If empty, socket.io connects to same-origin.
	VITE_SOCKET_URL: z.string().optional().transform(normalizeSocketUrl),
});

export const config = configSchema.parse(import.meta.env);
